import { BASE_URL } from '../config/api-routes';
import { isDjangoBackend, isNodeBackend, supportsDedicatedChatSocket } from './backendConfig';
import { inferAttachmentMediaKind } from './messageAttachments';

export { supportsDedicatedChatSocket };

export type V1SocketHandler = (payload: Record<string, unknown>) => void;

function wsBaseUrl(): string {
  const explicit = (process.env.REACT_APP_WS_URL || '').trim();
  if (explicit) return explicit.replace(/\/$/, '');
  const http = (BASE_URL || 'http://localhost:8000/').trim();
  if (http.startsWith('https://')) {
    return http.replace('https://', 'wss://').replace(/\/$/, '');
  }
  return http.replace('http://', 'ws://').replace(/\/$/, '');
}

function accessToken(): string | null {
  return localStorage.getItem('accessToken');
}

export type ChatSocketCallbacks = {
  onMessage?: (message: Record<string, unknown>) => void;
  onStatus?: (messageId: number, status: string) => void;
  onTyping?: (userId: number, isTyping: boolean) => void;
  onPresence?: (userId: number, isOnline: boolean) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

export type ChatConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export type PersistentChatSocketCallbacks = ChatSocketCallbacks & {
  onConnectionChange?: (state: ChatConnectionState) => void;
  /** When false, dropped sockets stay idle instead of reconnecting in a loop. */
  shouldReconnect?: () => boolean;
};

export type PersistentChatSocket = {
  getSocket(): WebSocket | null;
  getState(): ChatConnectionState;
  reconnectIfNeeded(): void;
  disconnect(): void;
};

const CHAT_RECONNECT_BASE_MS = 1000;
const CHAT_RECONNECT_MAX_MS = 30000;

function chatReconnectDelayMs(attempt: number): number {
  const exp = Math.min(CHAT_RECONNECT_MAX_MS, CHAT_RECONNECT_BASE_MS * 2 ** attempt);
  return exp + Math.floor(Math.random() * 400);
}

function attachChatSocketHandlers(ws: WebSocket, callbacks: ChatSocketCallbacks) {
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as Record<string, unknown>;
      if (data.type === 'chat_message' && data.message) {
        callbacks.onMessage?.(data.message as Record<string, unknown>);
      }
      if (data.type === 'message_status') {
        callbacks.onStatus?.(Number(data.message_id), String(data.status));
      }
      if (data.type === 'typing') {
        callbacks.onTyping?.(Number(data.user_id), Boolean(data.is_typing));
      }
      if (data.type === 'presence') {
        callbacks.onPresence?.(Number(data.user_id), Boolean(data.is_online));
      }
      if (data.type === 'pong') {
        return;
      }
    } catch (err) {
      console.error('Chat WS parse error', err);
    }
  };
}

export type ChatSendPayload = {
  content?: string;
  attachment_url?: string;
  attachment_type?: string;
  chatRequestId?: string;
};

type QuestionWsPending = {
  resolve: (value: Record<string, unknown>) => void;
  reject: (reason: Error) => void;
  timer: number;
};

const questionWsPending = new Map<string, QuestionWsPending>();
const QUESTION_WS_TIMEOUT_MS = 15000;

function handleQuestionWsResponse(data: Record<string, unknown>): boolean {
  const type = String(data.type || '');
  if (type !== 'ask_question_result' && type !== 'answer_question_result') {
    return false;
  }
  const requestId = String(data.request_id || '');
  const pending = questionWsPending.get(requestId);
  if (!pending) {
    return true;
  }
  clearTimeout(pending.timer);
  questionWsPending.delete(requestId);
  if (data.success) {
    pending.resolve(data);
  } else {
    pending.reject(new Error(String(data.message || 'Question action failed')));
  }
  return true;
}

export function supportsProfileQuestionWs(): boolean {
  return isDjangoBackend();
}

export function sendProfileQuestionWsAction(
  ws: WebSocket | null,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    return Promise.reject(new Error('Question WebSocket is not connected.'));
  }
  const requestId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      questionWsPending.delete(requestId);
      reject(new Error('Question WebSocket request timed out.'));
    }, QUESTION_WS_TIMEOUT_MS);
    questionWsPending.set(requestId, { resolve, reject, timer });
    ws.send(JSON.stringify({ ...payload, request_id: requestId }));
  });
}

export function connectNotificationSocket(
  handlers: Record<string, V1SocketHandler>
): WebSocket | null {
  const token = accessToken();
  if (!token) return null;

  const url = `${wsBaseUrl()}/ws/notifications/?token=${encodeURIComponent(token)}`;
  const ws = new WebSocket(url);

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as Record<string, unknown>;
      if (data.type === 'pong') return;
      if (handleQuestionWsResponse(data)) return;
      const eventName = String(data.event || data.type || '');
      if (eventName && handlers[eventName]) {
        handlers[eventName](data);
        return;
      }
      if (data.type === 'notification' && handlers.notification) {
        handlers.notification(data);
      }
    } catch (err) {
      console.error('Notification WS parse error', err);
    }
  };

  return ws;
}

const NOTIFICATION_HEARTBEAT_MS = 30_000;
const CHAT_HEARTBEAT_MS = 25_000;

/** Keep notifications WS presence fresh; returns cleanup. */
export function attachNotificationHeartbeat(ws: WebSocket): () => void {
  const ping = () => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'ping' }));
    }
  };
  ping();
  const timer = window.setInterval(ping, NOTIFICATION_HEARTBEAT_MS);
  return () => window.clearInterval(timer);
}

/** Refresh chat-room presence while the socket is open; returns cleanup. */
export function attachChatHeartbeat(ws: WebSocket): () => void {
  const ping = () => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'ping' }));
    }
  };
  ping();
  const timer = window.setInterval(ping, CHAT_HEARTBEAT_MS);
  return () => window.clearInterval(timer);
}

export function connectChatSocket(
  conversationUuid: string,
  callbacks: ChatSocketCallbacks
): WebSocket | null {
  if (isNodeBackend() || !supportsDedicatedChatSocket()) {
    return null;
  }
  const token = accessToken();
  if (!token || !conversationUuid) return null;

  const url = `${wsBaseUrl()}/ws/chat/${conversationUuid}/?token=${encodeURIComponent(token)}`;
  const ws = new WebSocket(url);

  ws.onopen = () => callbacks.onOpen?.();
  ws.onclose = () => callbacks.onClose?.();
  attachChatSocketHandlers(ws, callbacks);

  return ws;
}

/** Keeps chat WS alive while the thread is open; reconnects on drop until disconnect(). */
export function createPersistentChatSocket(
  conversationUuid: string,
  callbacks: PersistentChatSocketCallbacks
): PersistentChatSocket | null {
  if (isNodeBackend() || !supportsDedicatedChatSocket()) {
    return null;
  }
  if (!conversationUuid || !accessToken()) return null;

  let ws: WebSocket | null = null;
  let intentionalClose = false;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let state: ChatConnectionState = 'connecting';
  let stopChatHeartbeat: (() => void) | null = null;

  const setState = (next: ChatConnectionState) => {
    state = next;
    callbacks.onConnectionChange?.(next);
  };

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (intentionalClose) return;
    if (callbacks.shouldReconnect && !callbacks.shouldReconnect()) {
      setState('disconnected');
      return;
    }
    clearReconnectTimer();
    setState('reconnecting');
    const delay = chatReconnectDelayMs(reconnectAttempt);
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      openSocket();
    }, delay);
  };

  const reconnectIfNeeded = () => {
    if (intentionalClose) return;
    if (ws?.readyState === WebSocket.OPEN) return;
    reconnectAttempt = 0;
    clearReconnectTimer();
    openSocket();
  };

  const openSocket = () => {
    if (intentionalClose) return;
    const token = accessToken();
    if (!token) return;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    setState(reconnectAttempt > 0 ? 'reconnecting' : 'connecting');
    const url = `${wsBaseUrl()}/ws/chat/${conversationUuid}/?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(url);
    ws = socket;
    attachChatSocketHandlers(socket, callbacks);

    socket.onopen = () => {
      reconnectAttempt = 0;
      stopChatHeartbeat?.();
      stopChatHeartbeat = attachChatHeartbeat(socket);
      setState('connected');
      callbacks.onOpen?.();
    };

    socket.onclose = () => {
      stopChatHeartbeat?.();
      stopChatHeartbeat = null;
      if (ws === socket) ws = null;
      if (intentionalClose) {
        setState('disconnected');
        callbacks.onClose?.();
        return;
      }
      callbacks.onClose?.();
      scheduleReconnect();
    };

    socket.onerror = () => {
      socket.close();
    };
  };

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible' || intentionalClose) return;
    if (callbacks.shouldReconnect && !callbacks.shouldReconnect()) return;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      reconnectAttempt = 0;
      clearReconnectTimer();
      openSocket();
    }
  };

  const onBrowserOnline = () => {
    if (intentionalClose) return;
    if (callbacks.shouldReconnect && !callbacks.shouldReconnect()) return;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      reconnectAttempt = 0;
      clearReconnectTimer();
      openSocket();
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onBrowserOnline);
  }

  openSocket();

  return {
    getSocket: () => (ws?.readyState === WebSocket.OPEN ? ws : null),
    getState: () => state,
    reconnectIfNeeded,
    disconnect: () => {
      intentionalClose = true;
      clearReconnectTimer();
      stopChatHeartbeat?.();
      stopChatHeartbeat = null;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onBrowserOnline);
      }
      ws?.close();
      ws = null;
      setState('disconnected');
    },
  };
}

export function sendChatTyping(ws: WebSocket | null, isTyping: boolean) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify({ type: 'typing', is_typing: isTyping }));
}

export function sendChatMarkSeen(ws: WebSocket | null) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify({ type: 'mark_seen' }));
}

export function sendChatPayload(ws: WebSocket | null, payload: ChatSendPayload) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify(payload));
}

export function formatWsMessage(raw: Record<string, unknown>) {
  const sender = (raw.sender as Record<string, unknown>) || {};
  const attachments: Array<Record<string, unknown>> = [];
  const attachmentUrl = raw.attachment_url as string | undefined;
  if (attachmentUrl) {
    const attMeta = {
      type: String(raw.attachment_type || 'file'),
      mimeType: String(raw.attachment_type || ''),
      fileUrl: attachmentUrl,
      fileName: 'attachment',
    };
    attachments.push({
      id: String(raw.id),
      fileName: 'attachment',
      fileUrl: attachmentUrl,
      type: inferAttachmentMediaKind(attMeta),
      mimeType: attMeta.mimeType || 'application/octet-stream',
    });
  }
  const kind = (raw.kind as string) || 'chat';
  const questionRaw = raw.question as Record<string, unknown> | undefined;
  const question =
    questionRaw && questionRaw.questionText
      ? {
          section: String(questionRaw.section || 'General'),
          questionText: String(questionRaw.questionText),
          answerText: questionRaw.answerText ? String(questionRaw.answerText) : null,
        }
      : null;
  return {
    id: String(raw.id),
    senderId: sender.id as number,
    content: (raw.content as string) || '',
    createdAt: (raw.created_at as string) || new Date().toISOString(),
    status: (raw.status as string) || 'sent',
    kind,
    question,
    attachments,
    chatRequestId:
      raw.chatRequestId != null
        ? String(raw.chatRequestId)
        : raw.chat_request_id != null
          ? String(raw.chat_request_id)
          : undefined,
    sender: {
      id: sender.id as number,
      firstName: (sender.first_name as string) || '',
      lastName: (sender.last_name as string) || '',
      email: (sender.email as string) || '',
      profileImg: (sender.profile_image as string) || null,
      role: (sender.role as string) || '',
    },
  };
}
